import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getTrackingLists } from '../../../redux/actions/trackingList/trackingListAction';
import { updateTrackingList } from '../../../redux/reducers/trackingList/trackingListSlice';
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from 'react-router-dom';
import { apiConstants, getDownload } from "../../../common/constants";
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { DownloadRounded, UploadFileRounded, SearchRounded } from '@mui/icons-material';
import axios from "axios";
import { useSnackbar } from 'notistack';
import { FONT, SIZE } from '../../../styles/foundation/typography';
import { uploadSectionSx, primaryBtnSx, secondaryBtnSx, btnRowSx, helpTextSx, helpTextTypeSx } from '../../../styles/patterns';
import { PRIMARY_BLUE } from '../../../styles/foundation/colors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const MAX_AWBS = 20;

const TrackBox = () => {
    const { register, handleSubmit, formState: { errors } } = useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const inputFile = useRef(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [awbError, setAwbError] = useState('');

    const onButtonClick = () => {
        inputFile.current.click();
    };
    const handleUpload = (e) => {
        setBulkLoading(true)
        navigate(`/tracking-list`);
        dispatch(getTrackingLists(e.target.files[0])).finally(() => {
            setBulkLoading(false)
        })
    };
    const onSubmit = async (data) => {
        let config = { headers: { "Content-Type": "application/json" } };
        let awb = (data.awb).replace(/\s/g, '');
        const awbList = awb.split(',').filter(Boolean);
        if (awbList.length > MAX_AWBS) {
            setAwbError(`You can track up to ${MAX_AWBS} AWBs at a time. Please use the Upload option below for bulk tracking (up to 5000 AWBs).`);
            return;
        }
        setAwbError('');
        setLoading(true);
        try {
            const url = `https://plccqrpm5g3nal4k752kggoqvq0wcyfc.lambda-url.ap-south-1.on.aws/?waybills=${(awb)}`;
            const response = await axios.get(url, config);
            if (response?.status === 200) {
                const data = response?.data;
                const payload = Array.isArray(data)
                    ? { Status: 200, waybillDetails: data }
                    : data;
                dispatch(updateTrackingList(payload));
                if (location.pathname !== '/tracking-list') navigate('/tracking-list');
            }
        } catch (error) {
            console.error('Error fetching tracking data:', error);
            enqueueSnackbar('Error fetching tracking data', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleBulkSample = () => {
        getDownload(
            `${apiConstants.BASE_LOCAL}${apiConstants.DOWNLOAD_SAMPLE_LOCATION}?fileName=TRACK_ORDER`
        )
            .then((response) => {
                const binaryData = [];
                binaryData.push(response.data);
                const href = URL.createObjectURL(
                    new Blob(binaryData, { type: "application/octet-stream" })
                );
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", "TRACK_ORDER.csv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    return (
        <>
            {/* AWB input section */}
            <Box sx={uploadSectionSx}>
                <Box sx={helpTextSx}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                    <Typography sx={helpTextTypeSx}>
                        <strong>Tip:</strong> Enter comma-separated AWB numbers (max {MAX_AWBS}) or upload a CSV file for bulk tracking (up to 5,000 AWBs).
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                        {...register('awb', {
                            required: { value: true, message: "You must enter an AWB number" }
                        })}
                        fullWidth
                        size="small"
                        placeholder="Enter one or multiple AWB numbers (comma separated)"
                        error={!!errors.awb}
                        helperText={errors.awb?.message}
                        sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: FONT, fontSize: SIZE.xs } }}
                    />
                    <Box sx={btnRowSx}>
                        <LoadingButton type="submit" variant="contained" size="small" sx={primaryBtnSx}
                            startIcon={<SearchRounded />} loading={loading}>
                            Track Order
                        </LoadingButton>
                        <input type="file" id="file" onChange={handleUpload} ref={inputFile} style={{ display: 'none' }} />
                        <LoadingButton variant="outlined" size="small" sx={secondaryBtnSx}
                            startIcon={<UploadFileRounded />} onClick={onButtonClick} loading={bulkLoading}>
                            Upload CSV
                        </LoadingButton>
                        <Button variant="outlined" size="small" sx={secondaryBtnSx}
                            startIcon={<DownloadRounded />} onClick={handleBulkSample}>
                            Sample Download
                        </Button>
                    </Box>
                </form>

                {awbError && (
                    <Alert severity="warning" sx={{ mt: 2, fontFamily: FONT, fontSize: SIZE['2xs'] }} onClose={() => setAwbError('')}>
                        {awbError}
                    </Alert>
                )}
            </Box>
        </>
    );
};
export default TrackBox;
